import { useState } from 'react';
import { Alert } from '../../../components/ui/Alert';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import {
  useAddCustomerOdooLink,
  useOdooPartnerSearch,
  useRemoveCustomerOdooLink,
} from '../../../hooks/useCustomers';
import type { CustomerRow, OdooLinkSource } from '../../../api/customers';

const LINKED_BY_LABEL: Record<OdooLinkSource, string> = {
  odoo_partner_id: 'linked from Attio',
  manual: 'linked by hand',
  name: 'matched by name',
};

/**
 * Which Odoo partners this customer is read from, with a picker to add one.
 * Most customers are one partner; Xerox is two (contracts on Xerox Corporation,
 * invoices on Lexmark), and a customer with none reads as unknown everywhere.
 */
export function OdooPartnerLinks({ customer }: { customer: CustomerRow }) {
  const [query, setQuery] = useState('');
  const search = useOdooPartnerSearch(query);
  const add = useAddCustomerOdooLink();
  const remove = useRemoveCustomerOdooLink();
  const linkedIds = new Set(customer.odooPartners.map((p) => p.odooPartnerId));

  const link = async (odooPartnerId: number) => {
    await add.mutateAsync({ crmName: customer.companyName, odooPartnerId });
    setQuery('');
  };

  return (
    <div>
      <p className="text-body-sm font-medium text-text-secondary mb-2">
        Odoo partners
        <span className="font-normal text-text-tertiary">
          : every Odoo customer record this customer's contracts and invoices are read from
        </span>
      </p>
      {(add.error || remove.error) && (
        <Alert variant="error">{(add.error ?? remove.error)?.message}</Alert>
      )}
      {customer.odooPartners.length === 0 ? (
        <p className="text-body-sm text-text-tertiary mb-2">
          None. Search the Odoo partner below and link it; until then nothing can be read.
        </p>
      ) : (
        <ul className="divide-y divide-border-light mb-2">
          {customer.odooPartners.map((partner) => (
            <li key={partner.companyId} className="flex items-center justify-between gap-3 py-1.5">
              <span className="text-body-sm text-text-primary">
                {partner.name}
                <span className="text-text-tertiary">
                  {' '}
                  · {LINKED_BY_LABEL[partner.linkedBy]}
                  {partner.odooPartnerId !== null && ` · #${partner.odooPartnerId}`}
                </span>
              </span>
              {partner.linkId && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => remove.mutate(partner.linkId!)}
                  disabled={remove.isPending}
                >
                  Unlink
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <Input
        label="Link another Odoo partner"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Type part of the Odoo partner name…"
        hint="Partners come from the Odoo mirror. A partner not listed here has not been synced yet."
      />
      {query.trim().length >= 2 && (
        <ul className="mt-1 max-h-40 overflow-y-auto rounded border border-border-light">
          {search.isLoading && <li className="px-3 py-1.5 text-body-sm text-text-tertiary">Searching…</li>}
          {search.data?.partners.length === 0 && (
            <li className="px-3 py-1.5 text-body-sm text-text-tertiary">No Odoo partner matches.</li>
          )}
          {search.data?.partners.map((partner) => (
            <li key={partner.companyId}>
              <button
                type="button"
                className="w-full px-3 py-1.5 text-left text-body-sm hover:bg-surface-secondary disabled:opacity-50"
                disabled={linkedIds.has(partner.odooPartnerId) || add.isPending}
                onClick={() => link(partner.odooPartnerId)}
              >
                {partner.name}
                <span className="text-text-tertiary"> · #{partner.odooPartnerId}</span>
                {linkedIds.has(partner.odooPartnerId) && (
                  <span className="text-text-tertiary"> · already linked</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
